/**
 * Find My über iCloud.com: Zustand und Anmeldung.
 *
 * Der Vertrauens-Token hält etwa zwei Monate. Läuft er ab, kommt ein
 * Gitea-Issue mit einem Link hierher — neu anmelden soll vom Telefon aus
 * gehen, ohne SSH. Apple-ID und Passwort lassen sich hier eintragen; Encom
 * legt sie in data/icloud/ ab (Modus 600) und gibt das Passwort nie zurück.
 *
 * Der Knopf ist bewusst kein beiläufiger: jeder Druck schickt einen Code an
 * alle Apple-Geräte der Familie. Encom sperrt ihn deshalb für fünf Minuten.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, KeyRound, Smartphone } from "lucide-react";

import { api, ApiFehler, type ICloudStatus } from "@/api.ts";
import { benutzer } from "@/auth.ts";
import { zeitpunktLokal } from "@/zeit.ts";

const TEXT: Record<ICloudStatus["zustand"], string> = {
  aus: "Nicht eingerichtet",
  startet: "Startet",
  bereit: "Läuft",
  "code-noetig": "Angehalten — neu anmelden",
  abgewiesen: "Apple lehnt die Anmeldung ab",
  fehler: "Vorübergehend gestört",
};

export function ICloudAnmeldung() {
  const [status, setStatus] = useState<ICloudStatus | null>(null);
  const [code, setCode] = useState("");
  const [appleId, setAppleId] = useState("");
  const [passwort, setPasswort] = useState("");
  const [zugangOffen, setZugangOffen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const admin = benutzer()?.role === "admin";

  const laden = useCallback(() => {
    api.icloud().then(setStatus).catch(() => setStatus(null));
  }, []);

  useEffect(() => {
    laden();
    const takt = setInterval(laden, 30_000);
    return () => clearInterval(takt);
  }, [laden]);

  // Der Link aus dem Issue zeigt auf #icloud — dorthin rollen, sobald die
  // Karte steht.
  const geladen = status !== null;
  useEffect(() => {
    if (geladen && window.location.hash === "#icloud") {
      document.getElementById("icloud")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [geladen]);

  const ausfuehren = async (schritt: () => Promise<ICloudStatus>) => {
    setLaeuft(true);
    setFehler(null);
    try {
      const neu = await schritt();
      setStatus(neu);
      // Der Hinweis in der Navigation fragt nur alle 15 Minuten nach — ohne
      // Bescheid stünde er nach gelungener Anmeldung noch so lange rot da.
      window.dispatchEvent(new CustomEvent("ribbon:icloud", { detail: neu }));
    } catch (e: unknown) {
      setFehler(e instanceof ApiFehler ? e.message : String(e));
    } finally {
      // Ein Einmalcode wird neu abgetippt, nicht korrigiert.
      setCode("");
      setLaeuft(false);
    }
  };

  if (!status) return null;

  // Speichern stößt gleich die Anmeldung an — sonst wäre es ein Klick mehr bis
  // zum Code, und genau der würde vergessen.
  const zugangSpeichern = () =>
    ausfuehren(async () => {
      await api.icloudZugang(appleId.trim(), passwort);
      setPasswort("");
      setZugangOffen(false);
      return api.icloudAnmelden();
    });

  const zeigeZugang = status.zustand === "aus" || status.zustand === "abgewiesen" || zugangOffen;

  const gut = status.zustand === "bereit";
  // Wartet eine Anmeldung auf ihren Code, ist das kein Stillstand, sondern der
  // Schritt, in dem man gerade steckt.
  const steht = !status.codeErwartet && (status.zustand === "code-noetig" || status.zustand === "abgewiesen");

  return (
    <section id="icloud" className="scroll-mt-4 rounded-lg border border-border bg-surface p-5">
      <h2 className="flex items-center gap-2 text-lg font-semibold">
        <Smartphone size={18} />
        Find My über iCloud.com
      </h2>
      <p className="mt-1 text-sm text-text-muted">
        Eigene Geräte und Familie, alle {status.intervallMin} Minuten frisch geortet — unabhängig
        davon, ob jemand am Mac sitzt. Im selben Takt holt Encom Zeitachsen-Exporte aus dem
        Hauptordner des iCloud Drive. AirTags und Freunde kommen weiter über den Mac.
      </p>

      <p
        className={`mt-4 flex items-start gap-2 text-sm ${
          gut ? "text-success" : steht ? "text-danger" : "text-text-muted"
        }`}
      >
        {gut ? (
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
        ) : (
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        )}
        <span>
          <span className="font-medium">
            {status.codeErwartet ? "Wartet auf den Zwei-Faktor-Code" : TEXT[status.zustand]}
          </span>
          {status.letzterAbrufMs !== null && (
            <span className="block text-text-muted">
              Letzter Abruf {zeitpunktLokal(new Date(status.letzterAbrufMs).toISOString())},{" "}
              {status.geraete} Geräte mit Position
            </span>
          )}
          {status.letzterFehler && status.zustand !== "bereit" && (
            <span className="block text-text-muted">{status.letzterFehler}</span>
          )}
          {status.appleId && (
            <span className="block text-text-muted">
              Apple-ID {status.appleId}
              {status.zugangAus === "env" && " (aus der .env)"}
            </span>
          )}
        </span>
      </p>

      {status.drive.letzterBlickMs !== null && (
        <p className="mt-3 text-sm text-text-muted">
          <span className="font-medium text-text">iCloud Drive:</span> zuletzt nachgesehen{" "}
          {zeitpunktLokal(new Date(status.drive.letzterBlickMs).toISOString())}
          {status.drive.letzterImport && (
            <span className="block">
              Zuletzt übernommen: {status.drive.letzterImport.dateiname},{" "}
              {zeitpunktLokal(new Date(status.drive.letzterImport.amMs).toISOString())} —{" "}
              {status.drive.letzterImport.schonBekannt
                ? "war schon bekannt"
                : `${status.drive.letzterImport.neu} neu, ${status.drive.letzterImport.revidiert} revidiert`}
            </span>
          )}
          {status.drive.fehler && (
            <span className="block text-danger">{status.drive.fehler}</span>
          )}
        </p>
      )}

      {admin && zeigeZugang && !status.codeErwartet && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void zugangSpeichern();
          }}
          className="mt-4 grid max-w-md gap-3"
        >
          <label className="grid gap-1 text-sm">
            Apple-ID
            <input
              value={appleId}
              onChange={(e) => setAppleId(e.target.value)}
              type="email"
              autoComplete="username"
              placeholder={status.appleId ?? "name@icloud.com"}
              required
              className="rounded-lg border border-border bg-page-bg px-3 py-2"
            />
          </label>
          <label className="grid gap-1 text-sm">
            Passwort
            <input
              value={passwort}
              onChange={(e) => setPasswort(e.target.value)}
              type="password"
              autoComplete="current-password"
              placeholder={status.appleId ? "leer lassen = unverändert" : ""}
              className="rounded-lg border border-border bg-page-bg px-3 py-2"
            />
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={laeuft || !appleId.trim()}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white hover:bg-primary-dark disabled:opacity-50"
            >
              <KeyRound size={16} />
              {laeuft ? "melde an…" : "Speichern und anmelden"}
            </button>
            {zugangOffen && (
              <button type="button" onClick={() => setZugangOffen(false)} className="text-sm text-text-muted hover:underline">
                Abbrechen
              </button>
            )}
          </div>
          <p className="text-xs text-text-muted">
            Liegt auf dem Server wie die .env (nur für den Dienst lesbar) und wird nie wieder
            angezeigt. Beim Anmelden kommt ein Code auf alle Apple-Geräte der Familie.
          </p>
        </form>
      )}

      {admin && !zeigeZugang && (
        <div className="mt-4">
          {status.codeErwartet ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void ausfuehren(() => api.icloudCode(code));
              }}
              className="flex flex-wrap items-center gap-3"
            >
              <label className="text-sm" htmlFor="icloud-code">
                {status.smsAn === null
                  ? "Code:"
                  : `Code aus der SMS an ${status.telefone.find((t) => t.id === status.smsAn)?.nummer ?? "dein Telefon"}:`}
              </label>
              <input
                id="icloud-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                placeholder="000000"
                className="w-28 rounded-lg border border-border bg-page-bg px-3 py-2 text-center font-mono tracking-widest"
              />
              <button
                type="submit"
                disabled={laeuft || code.length !== 6}
                className="rounded-lg bg-primary px-4 py-2 text-sm text-white hover:bg-primary-dark disabled:opacity-50"
              >
                {laeuft ? "prüfe…" : "Bestätigen"}
              </button>
              {status.smsAn === null && status.telefone.length > 0 && (
                <p className="w-full text-xs text-text-muted">
                  Code anfordern:{" "}
                  {status.telefone.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      disabled={laeuft}
                      onClick={() => void ausfuehren(() => api.icloudSms(t.id))}
                      className="mr-2 underline hover:text-text disabled:opacity-50"
                    >
                      Per SMS an {t.nummer}
                    </button>
                  ))}
                </p>
              )}
            </form>
          ) : (
            <button
              onClick={() => void ausfuehren(api.icloudAnmelden)}
              disabled={laeuft}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm disabled:opacity-50 ${
                steht
                  ? "bg-primary text-white hover:bg-primary-dark"
                  : "border border-border hover:bg-page-bg"
              }`}
            >
              <KeyRound size={16} />
              {laeuft ? "melde an…" : steht ? "Bei iCloud anmelden" : "Neu anmelden"}
            </button>
          )}
          {!status.codeErwartet && (
            <p className="mt-2 text-xs text-text-muted">
              Danach „Per SMS" wählen — auf den Geräten erscheint kein Code.{" "}
              <button onClick={() => setZugangOffen(true)} className="underline hover:text-text">
                Apple-ID oder Passwort ändern
              </button>
            </p>
          )}
        </div>
      )}

      {fehler && (
        <p className="mt-3 flex items-start gap-2 text-sm text-danger">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          {fehler}
        </p>
      )}
    </section>
  );
}

/**
 * Hinweis in der Navigation, wenn der Abruf steht. Ohne ihn sähe ein
 * angehaltener Abruf in der Live-Ansicht aus wie Geräte, die sich nicht
 * bewegen — Schweigen muss sich von Funktionieren unterscheiden.
 */
export function ICloudHinweis() {
  const [status, setStatus] = useState<ICloudStatus | null>(null);

  useEffect(() => {
    let weg = false;
    const laden = () =>
      api
        .icloud()
        .then((s) => !weg && setStatus(s))
        .catch(() => {
          // Die Anzeige bleibt dann einfach leer.
        });
    laden();
    const takt = setInterval(laden, 15 * 60_000);
    const bescheid = (e: Event) => setStatus((e as CustomEvent<ICloudStatus>).detail);
    window.addEventListener("ribbon:icloud", bescheid);
    return () => {
      weg = true;
      clearInterval(takt);
      window.removeEventListener("ribbon:icloud", bescheid);
    };
  }, []);

  // Auch ein Fehler, der seit über einer Stunde anhält, ist ein Stillstand —
  // vom 27. bis 30.09. stand der Abruf so, ohne dass hier etwas erschien.
  const lange = status?.zustand === "fehler"
    && Date.now() - (status.letzterAbrufMs ?? status.seitMs) > 60 * 60_000;
  const wartet = status?.zustand === "code-noetig" || status?.zustand === "abgewiesen";
  if (!wartet && !lange) return null;

  return (
    <Link
      to="/archiv#icloud"
      className="mx-2 mb-2 flex items-start gap-2 rounded-lg bg-danger/15 px-3 py-2 text-xs text-red-300 hover:bg-danger/25"
    >
      <AlertTriangle size={14} className="mt-0.5 shrink-0" />
      <span className="min-w-0">
        <span className="block font-medium">iCloud-Abruf steht</span>
        <span className="block opacity-80">{wartet ? "Neu anmelden" : "Störung — siehe Archiv"}</span>
      </span>
    </Link>
  );
}
